import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

import { adminDashboardStore, DEFAULT_TIME_RANGE } from '~/store/adminDashboardStore';
import { type TimeRange } from '~/services/administrator/useSchoolBehaviorReportQuery';

export type { TimeRange };

/** Time-range options shared by behavior-chart pages. Centralized so the admin
 *  overview and student-detail pages stay in sync as we add or rename ranges.
 *  Labels match the teacher endpoints' "rolling N-day" semantics. */
export const TIME_RANGE_OPTIONS: { label: string; value: TimeRange }[] = [
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'Last year', value: '365d' },
];

const VALID_RANGES = new Set<TimeRange>(TIME_RANGE_OPTIONS.map((o) => o.value));

const labelFor = (range: TimeRange): string =>
  TIME_RANGE_OPTIONS.find((o) => o.value === range)?.label?.toLowerCase() ?? range;

/** Cross-page time-range selection for the admin dashboard.
 *
 *  Source-of-truth precedence: a valid `?range=` URL param wins (so deep-links
 *  are shareable); otherwise the Zustand store's last-set value is used (so
 *  the choice persists across navigation between Overview and Student Detail
 *  even when the URL drops the param). One-time mount sync pushes URL→store
 *  (deep-link rehydration) or store→URL (cross-nav rehydration). After mount,
 *  setTimeRange is the only writer to both — a continuous mirror effect races
 *  against the user's clicks (URL and store commit in different render passes,
 *  the effect re-fires mid-batch and reverts user input). */
export function useAdminTimeRange(): {
  timeRange: TimeRange;
  setTimeRange: (range: TimeRange) => void;
  timeRangeLabel: string;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const storeRange = adminDashboardStore((s) => s.timeRange);
  const setStoreRange = adminDashboardStore((s) => s.setTimeRange);

  const rawParam = searchParams.get('range');
  const urlRange: TimeRange | null =
    rawParam !== null && VALID_RANGES.has(rawParam as TimeRange) ? (rawParam as TimeRange) : null;

  const timeRange: TimeRange = urlRange ?? storeRange;

  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) return;
    didMountRef.current = true;
    if (urlRange !== null && urlRange !== storeRange) {
      setStoreRange(urlRange);
    } else if (urlRange === null && storeRange !== DEFAULT_TIME_RANGE) {
      setSearchParams({ range: storeRange }, { replace: true });
    }
  }, [urlRange, storeRange, setStoreRange, setSearchParams]);

  const setTimeRange = (range: TimeRange) => {
    setStoreRange(range);
    setSearchParams(range === DEFAULT_TIME_RANGE ? {} : { range }, { replace: true });
  };

  return { timeRange, setTimeRange, timeRangeLabel: labelFor(timeRange) };
}
